import { Box, Divider, Flex, HStack, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Text, useColorModeValue } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RecognitionDraftCard, RecognitionDraftVariant } from '../../types/ui-blocks';
import { useBizyChatActions, useBizyUiBlockActionDispatcher } from '../context/actions-context';
import type { BizyGif } from '../context/gif-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { DotsHorizontalOutline, TrashOutline } from '../lib/icons';
import { RECOGNITION_DRAFT_FOCUS_TAB_PROPS } from '../lib/recognition-draft-focus-tab-props';
import { AmountPill, HashtagToken, RecipientHandle } from '../lib/recognition-segments';
import { UiBlockActionBar } from '../lib/ui-block-action-bar';
import { GifPicker } from './gif-picker-card';
import type { BizyNodeProps } from './node-props';

/** Hand-rolled focus/tone tab (replaces the app's PillTab) — pill toggle styled with theme tokens. */
function FocusTab({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) {
	const activeBg = useColorModeValue('secondary.bg', 'white');
	const activeColor = useColorModeValue('text.onColor', 'gray.900');
	return (
		<Flex
			as="button"
			type="button"
			role="tab"
			aria-selected={isActive}
			onClick={onClick}
			flexShrink={0}
			align="center"
			bg={isActive ? activeBg : 'transparent'}
			borderColor={isActive ? 'transparent' : 'border.default'}
			borderRadius="full"
			borderStyle="solid"
			borderWidth="1px"
			color={isActive ? activeColor : 'text.default'}
			_hover={isActive ? { bg: activeBg } : { bg: 'secondary.bgAlpha' }}
			{...RECOGNITION_DRAFT_FOCUS_TAB_PROPS}
		>
			{label}
		</Flex>
	);
}

/**
 * Recognition draft card — the user is writing a recognition and Bizy is
 * offering a drafted message. When the payload carries multiple `variants`
 * the card shows a tab per focus; selecting one swaps the body + actions
 * client-side. An embedded gif picker (when a sibling gif_picker_card is
 * present) lets the user attach a gif via the `attach_gif` action.
 */
export function RecognitionDraftCardNode({ node, embeddedGifPicker }: BizyNodeProps) {
	const block = node as RecognitionDraftCard;
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const dispatch = useBizyUiBlockActionDispatcher();
	const { fontSize = 'sm', isReadOnly } = useBizyChatActions();
	const gifPicker = embeddedGifPicker;
	const { giver, recipient, amount, message, hashtags, actions, variants, attachment, coachingPrompt } = block.payload;
	const [activeIndex, setActiveIndex] = useState(0);
	const [attachPending, setAttachPending] = useState(false);

	const attachmentKey = attachment?.gif ?? null;
	const [pendingForAttachment, setPendingForAttachment] = useState(attachmentKey);
	if (pendingForAttachment !== attachmentKey) {
		setPendingForAttachment(attachmentKey);
		setAttachPending(false);
	}

	const hasTabs = (variants?.length ?? 0) > 1;
	const fallbackVariant: RecognitionDraftVariant = { focus: null, message, amount, hashtags, actions, qualityScore: null, coachingPrompt };
	const safeIndex = Math.min(activeIndex, Math.max(0, (variants?.length ?? 1) - 1));
	const active = variants?.[safeIndex] ?? fallbackVariant;

	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const activeMessage = active.message;
	const activeAmount = active.amount;
	const activeActions = active.actions ?? [];
	const activeCoachingPrompt = hasTabs ? active.coachingPrompt : (active.coachingPrompt ?? coachingPrompt);
	const hashtagList = (active.hashtags ?? []).map((h) => h.replace(/^#/, ''));

	const copyText = [
		activeAmount && activeAmount > 0 ? `+${activeAmount}` : null,
		...recipients.map((r) => (r.startsWith('@') ? r : `@${r}`)),
		activeMessage,
		...hashtagList.map((h) => `#${h}`),
	]
		.filter(Boolean)
		.join(' ');

	const dispatchAttachGif = (nextAttachment: Record<string, unknown> | null) => {
		if (attachPending) return;
		setAttachPending(true);
		dispatch({
			type: 'attach_gif',
			payload: {
				recipient,
				amount: amount ?? null,
				message,
				hashtags: hashtags ?? [],
				actions: actions ?? [],
				...(variants ? { variants } : {}),
				...(coachingPrompt ? { coachingPrompt } : {}),
				pills: gifPicker?.pills ?? [],
				...(gifPicker?.primaryQuery ? { primaryQuery: gifPicker.primaryQuery } : {}),
				attachment: nextAttachment,
			},
		});
	};
	const handleSelectGif = (gif: BizyGif) => dispatchAttachGif(gif.attachment);
	const handleRemoveGif = () => dispatchAttachGif(null);

	return (
		<Box my={2}>
			<Box px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
				{hasTabs && (
					<>
						<Box overflowX="auto" mb={3} fontSize="sm" lineHeight="5" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
							<HStack role="tablist" spacing={2}>
								{variants?.map((v, i) => (
									<FocusTab
										key={`${v.focus ?? 'focus'}-${i}`}
										isActive={i === safeIndex}
										onClick={() => setActiveIndex(i)}
										label={v.focus || t('uiBlocks.recognitionDraft.focusFallback', { index: i + 1 })}
									/>
								))}
							</HStack>
						</Box>
						<Divider borderColor="border.default" width="auto" mx={-3} mb={3} />
					</>
				)}
				<Text fontSize={fontSize} lineHeight="tall" whiteSpace="pre-wrap">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{activeAmount != null && activeAmount > 0 && (
						<>
							<AmountPill amount={activeAmount} />{' '}
						</>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{activeMessage}
					{hashtagList.length > 0 && (
						<>
							{' '}
							{hashtagList.map((h, i) => (
								<span key={`${h}-${i}`}>
									<HashtagToken hashtag={h} />
									{i < hashtagList.length - 1 ? ' ' : ''}
								</span>
							))}
						</>
					)}
				</Text>
				{attachment?.gif && (
					<Box mt={2} position="relative" role="group" w="fit-content" maxW="100%" mx="auto">
						<Box borderRadius="lg" overflow="hidden">
							<Image src={attachment.gif} alt={attachment.altText ?? ''} display="block" maxH="220px" maxW="100%" objectFit="contain" w="auto" />
						</Box>
						{!isReadOnly && (
							<Menu>
								<MenuButton
									as={IconButton}
									icon={<DotsHorizontalOutline boxSize={4} />}
									aria-label={t('uiBlocks.gifPicker.options')}
									position="absolute"
									top={1}
									right={1}
									size="xs"
									isRound
									variant="solid"
									bg="blackAlpha.600"
									color="white"
									_hover={{ bg: 'blackAlpha.700' }}
								/>
								<MenuList minW="150px" p={1} border="none">
									<MenuItem onClick={handleRemoveGif} borderRadius="md" color="error.fg">
										<HStack spacing={2}>
											<TrashOutline boxSize={4} />
											<Text>{t('uiBlocks.gifPicker.remove')}</Text>
										</HStack>
									</MenuItem>
								</MenuList>
							</Menu>
						)}
					</Box>
				)}
				<UiBlockActionBar actions={activeActions} copyText={copyText} areActionsDisabled={isReadOnly} />
			</Box>
			{activeCoachingPrompt && (
				<Text mt={2} fontSize={fontSize} color="text.default" lineHeight="short">
					{activeCoachingPrompt}
				</Text>
			)}
			{gifPicker && !isReadOnly && !attachment?.gif && !attachPending && (
				<Box mb={2}>
					<GifPicker pills={gifPicker.pills} primaryQuery={gifPicker.primaryQuery} onSelectGif={handleSelectGif} />
				</Box>
			)}
		</Box>
	);
}
